import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdPersonAdd, MdDirectionsCar, MdUpload, MdSearch, MdPeople, MdLock, MdVisibility, MdEdit, MdLockOpen, MdClose, MdArrowUpward, MdArrowDownward } from 'react-icons/md';
import PageHeader from '../components/PageHeader';
import SkeletonRow from '../components/SkeletonRow';
import StatusToggle from '../../../Components/StatusToggle';
import Pagination from '../components/Pagination';
import { userApi, routesApi, locationApi, departmentApi } from '../../../api/adminapi';
import { getActiveRoleNames } from '../../../api/roles';
import '../Admin.css';
import './BookRoom.css';
import './Users.css';

function Users({ role }) {
  const navigate = useNavigate();
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [users, setUsers] = useState([]);
  const [userStatuses, setUserStatuses] = useState({}); // Track individual user statuses
  const [selectedUserIds, setSelectedUserIds] = useState(new Set());
  const [filters, setFilters] = useState({ query: '', department: '', location: '' });
  const [formData, setFormData] = useState({
    firstName: '',
    secondName: '',
    email: '',
    phone: '',
    empId: '',
    password: '',
    role: '',
    department: '',
    location: ''
  });
  // Stable dropdown options that do not shrink when filters are applied
  const [allDepartments, setAllDepartments] = useState([]);
  const [isLoadingDepartments, setIsLoadingDepartments] = useState(false);
  const [allLocations, setAllLocations] = useState([]);
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [isLoadingRoles, setIsLoadingRoles] = useState(false);
  const [deactivateData, setDeactivateData] = useState({
    userId: null,
    userName: '',
    reason: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isAssigning, setIsAssigning] = useState(false);

  const [isTableLoading, setIsTableLoading] = useState(false);

  // Sorting state
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'

  // Assign Cab modal state (UI only)
  const [showAssignCabModal, setShowAssignCabModal] = useState(false);
  const [assignForm, setAssignForm] = useState({ dropStation: '', vehicle: '' });
  const [dropStationOptions, setDropStationOptions] = useState([]);
  const [isDropStationsLoading, setIsDropStationsLoading] = useState(false);
  const [assignedVehiclesOptions, setAssignedVehiclesOptions] = useState([]);
  const [isVehiclesLoading, setIsVehiclesLoading] = useState(false);
  
  
  
  // Bulk import state
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [isBulkImporting, setIsBulkImporting] = useState(false);

  const handleOpenBulkImport = () => {
    setShowBulkImportModal(true);
    setBulkFile(null);
    setError('');
    setSuccessMessage('');
  };

  const handleCloseBulkImport = () => {
    setShowBulkImportModal(false);
    setBulkFile(null);
  };

  const handleBulkFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    setBulkFile(file || null);
  };

  const handleBulkImport = async () => {
    if (!bulkFile) {
      setError('Please select an Excel file to import.');
      try {
        const { toast } = await import('react-toastify');
        toast.warn('Please select an Excel file to import.', { position: 'top-right' });
      } catch {}
      return;
    }
    try {
      setIsBulkImporting(true);
      setError('');
      setSuccessMessage('');

      // Dynamically import xlsx to parse Excel files
      let XLSX;
      try {
        const mod = await import('xlsx');
        XLSX = mod && (mod.default || mod);
      } catch (impErr) {
        const msg = 'xlsx package is not installed. Please run: npm install xlsx';
        setError(msg);
        try {
          const { toast } = await import('react-toastify');
          toast.error(msg, { position: 'top-right' });
        } catch {}
        return;
      }
      const buffer = await bulkFile.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const rows = XLSX.utils.sheet_to_json(worksheet, { defval: '' });
      if (!Array.isArray(rows) || rows.length === 0) {
        setError('No rows found in the selected sheet.');
        return;
      }

      // Map rows to RegistrationDTO-like objects
      const users = rows.map((r) => ({
        firstName: r.firstName || r.FirstName || r.first_name || r['First Name'] || '',
        secondName: r.secondName || r.SecondName || r.last_name || r['Second Name'] || r['Last Name'] || '',
        email: (r.email || r.Email || '').toString().trim(),
        phone: (r.phone || r.Phone || '').toString().trim(),
        empId: (r.empId || r.EmpId || r.employeeId || r['Employee Id'] || '').toString().trim(),
        role: r.role || r.Role || '',
        department: r.department || r.Department || '',
        location: r.location || r.Location || '',
        password: r.password || r.Password || ''
      }));

      // Filter out completely empty rows
      const cleaned = users.filter(u => Object.values(u).some(v => (v ?? '').toString().trim() !== ''));

      if (cleaned.length === 0) {
        setError('No valid rows found in the selected file.');
        return;
      }

      // Call backend bulk API
      const resp = await userApi.bulkRegisterUsers(cleaned);
      const payload = resp?.data || resp; // handle ResponseHandler wrapper
      const successCount = payload?.successCount ?? 0;
      const failureCount = payload?.failureCount ?? 0;

      try {
        const { toast } = await import('react-toastify');
        toast.success(`Bulk import completed. Success: ${successCount}, Failed: ${failureCount}`, { position: 'top-right' });
      } catch {}

      setShowBulkImportModal(false);
      await handleApplyFilters(currentPage);
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || 'Bulk import failed';
      setError(msg);
      try {
        const { toast } = await import('react-toastify');
        toast.error(msg, { position: 'top-right' });
      } catch {}
    } finally {
      setIsBulkImporting(false);
    }
  };

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10); // Default page size, will be updated from backend response
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Fetch active locations, departments, and roles from backend on component mount
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        setIsLoadingLocations(true);
        const response = await locationApi.getAllActiveLocationNames();
        const locationNames = response?.data || response || [];
        setAllLocations(locationNames);
      } catch (err) {
        console.error('Failed to fetch locations:', err);
        // Fallback to empty array, locations can still be derived from users
        setAllLocations([]);
      } finally {
        setIsLoadingLocations(false);
      }
    };

    const fetchDepartments = async () => {
      try {
        setIsLoadingDepartments(true);
        const response = await departmentApi.getActiveCodes();
        const deptCodes = response?.data || response || [];
        setAllDepartments(Array.isArray(deptCodes) ? deptCodes : []);
      } catch (err) {
        console.error('Failed to fetch departments:', err);
        // No fallback - departments must come from department master
        setAllDepartments([]);
      } finally {
        setIsLoadingDepartments(false);
      }
    };

    const fetchRoles = async () => {
      try {
        setIsLoadingRoles(true);
        // Fetch all roles from custom_roles table (includes system and custom roles)
        const allRoles = await getActiveRoleNames();
        setAvailableRoles(allRoles);
      } catch (err) {
        console.error('Failed to fetch roles:', err);
        setAvailableRoles([]);
      } finally {
        setIsLoadingRoles(false);
      }
    };

    fetchLocations();
    fetchDepartments();
    fetchRoles();
  }, []);

  const handleAddUser = () => {
    setIsEditMode(false);
    setEditingUserId(null);
    setShowAddUserModal(true);
    setError('');
    setSuccessMessage('');
  };

  // Load users on initial mount
  useEffect(() => {
    handleApplyFilters();
  }, []);

  // Refetch when server-side filters change (including search)
  useEffect(() => {
    setCurrentPage(1);
    handleApplyFilters(1, pageSize);
  }, [filters.department, filters.location]);

  // Debounced search - trigger server-side search when user types
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      setCurrentPage(1);
      handleApplyFilters(1, pageSize);
    }, 300); // 300ms debounce

    return () => clearTimeout(debounceTimer);
  }, [filters.query]);

  // Refetch when sorting changes
  useEffect(() => {
    if (sortColumn) {
      setCurrentPage(1);
      handleApplyFilters(1, pageSize);
    }
  }, [sortColumn, sortDirection]);

  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    handleApplyFilters(newPage);
  };

  const handlePageSizeChange = (e) => {
    const newSize = parseInt(e.target.value, 10) || 10;
    setPageSize(newSize);
    setCurrentPage(1);
    handleApplyFilters(1, newSize);
  };

  // Selection helpers
  const isUserSelected = (userId) => selectedUserIds.has(userId);
  const toggleUserSelected = (user) => {
    const userId = user.id || user.empId;
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const handleAssignCab = () => {
    if (selectedUserIds.size === 0) {
      (async () => {
        try {
          const { toast } = await import('react-toastify');
          toast.warn('Please first select the user by clicking the checkbox');
        } catch (e) {
          // Fallback if toast fails to load
          setError('Please first select the user by clicking the checkbox');
        }
      })();
      return;
    }
    // Always open with a fresh form
    setAssignForm({ dropStation: '', vehicle: '' });
    setShowAssignCabModal(true);
  };

  const handleCloseAssignCab = () => {
    setShowAssignCabModal(false);
    setAssignForm({ dropStation: '', vehicle: '' });
  };

  const handleAssignChange = (e) => {
    const { name, value } = e.target;
    setAssignForm((prev) => ({ ...prev, [name]: value }));
    if (name === 'dropStation') {
      // Parse selected route label: "source → destination (timing)"
      try {
        const match = value.match(/^(.*?)\s*→\s*(.*?)\s*(?:\((.*?)\))?$/);
        const source = match?.[1]?.trim() || undefined;
        const destination = match?.[2]?.trim() || undefined;
        const timing = match?.[3]?.trim() || undefined;
        if (source && destination) {
          setIsVehiclesLoading(true);
          setAssignedVehiclesOptions([]);
          (async () => {
            try {
              const data = await routesApi.getAssignedVehiclesByFilters({ source, destination, timing });
              let list = [];
              if (Array.isArray(data)) list = data;
              else if (data?.data && Array.isArray(data.data)) list = data.data;
              setAssignedVehiclesOptions(list || []);
            } catch (err) {
              console.warn('Failed to load assigned vehicles:', err);
              setAssignedVehiclesOptions([]);
            } finally {
              setIsVehiclesLoading(false);
            }
          })();
        } else {
          setAssignedVehiclesOptions([]);
        }
      } catch (err) {
        console.warn('Failed parsing route label', err);
        setAssignedVehiclesOptions([]);
      }
    }
  };

  const handleAssignConfirm = async () => {
    if (!assignForm.dropStation || !assignForm.vehicle) return;
    const ids = Array.from(selectedUserIds);
    if (ids.length === 0) return;

    try {
      setIsAssigning(true);
      setError('');
      const resp = await userApi.assignVehicleAndStation(
        ids,
        assignForm.vehicle,
        assignForm.dropStation
      );
      console.log('Assign response:', resp);
      setSuccessMessage(`Drop and pickup station as well as vehicle have been assigned. Station: ${assignForm.dropStation}, Vehicle: ${assignForm.vehicle}`);
      const assignedCount = ids.length;
      alert(`Ok, drop station and vehicle have been assigned for ${assignedCount} user${assignedCount > 1 ? 's' : ''}.\nStation: ${assignForm.dropStation}\nVehicle: ${assignForm.vehicle}`);
      // Reset all selected checkboxes
      setSelectedUserIds(new Set());
      setTimeout(() => setSuccessMessage(''), 2500);
      setShowAssignCabModal(false);
      // Optional: refresh list
      await handleApplyFilters(currentPage);
    } catch (e) {
      console.error('Assign failed', e);
      setError(e?.response?.data?.message || e.message || 'Assignment failed');
    } finally {
      setIsAssigning(false);
    }
  };

  const handleApplyFilters = async (page = currentPage, size = pageSize) => {
    try {
      setIsTableLoading(true);
      setError('');
      const data = await userApi.getUsersByCompany(page, size, null, {
        department: filters.department || undefined,
        location: filters.location || undefined,
        sortBy: sortColumn || undefined,
        sortDirection: sortDirection || undefined,
        search: filters.query || undefined,
      });
      console.log('API Response:', data);
      console.log('Full response structure:', JSON.stringify(data, null, 2));
      
      // Extract users data from response - handle Spring Boot pagination structure
      let usersData = [];
      let paginationInfo = {};
      
      if (data?.data?.content && Array.isArray(data.data.content)) {
        // Spring Boot pagination structure: { data: { content: [...], totalElements: X, totalPages: Y, ... } }
        usersData = data.data.content;
        paginationInfo = {
          currentPage: data.data.number + 1, // Spring Boot uses 0-based indexing
          pageSize: data.data.size,
          totalPages: data.data.totalPages,
          totalItems: data.data.totalElements
        };
      } else if (data?.data && Array.isArray(data.data)) {
        // Structure: { data: [...], pagination: {...} }
        usersData = data.data;
        paginationInfo = data.pagination || {};
      } else if (Array.isArray(data)) {
        // Structure: [...]
        usersData = data;
      } else if (data?.users && Array.isArray(data.users)) {
        // Structure: { users: [...], pagination: {...} }
        usersData = data.users;
        paginationInfo = data.pagination || {};
      } else if (data?.content && Array.isArray(data.content)) {
        // Structure: { content: [...], pagination: {...} }
        usersData = data.content;
        paginationInfo = data.pagination || {};
      } else {
        console.warn('Unexpected API response structure:', data);
        usersData = [];
      }
      
      console.log('Extracted users data:', usersData);
      console.log('Extracted pagination info:', paginationInfo);
      
      setUsers(usersData);
      setCurrentPage(paginationInfo.currentPage || page);
      setPageSize(paginationInfo.pageSize || size);
      setTotalPages(paginationInfo.totalPages || 1);
      setTotalItems(paginationInfo.totalItems || usersData.length);
      
      // Expand location options based on fetched users (departments come only from department master)
      const derivedLocations = Array.from(new Set(usersData.map(u => u?.location).filter(Boolean)));
      setAllLocations(prev => Array.from(new Set([...(prev || []), ...derivedLocations])));
      
      // Initialize all users as active by default
      const initialStatuses = {};
      usersData.forEach(user => {
        initialStatuses[user.id || user.empId] = user.status || 'ACTIVE';
      });
      setUserStatuses(initialStatuses);

    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to fetch users');
    } finally {
      setIsTableLoading(false);
    }
  };

  // Sorting handler
  const handleSort = (column) => {
    if (sortColumn === column) {
      // Toggle direction if same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Users list - server handles filtering and sorting
  const filteredUsers = useMemo(() => {
    if (!users || users.length === 0) return [];
    // Server handles search, filtering, and sorting - just return users
    return users;
  }, [users]);

  // Selection derived values (must come after filteredUsers is defined)
  const visibleUserIds = useMemo(() => (Array.isArray(filteredUsers) ? filteredUsers.map((u) => u.id || u.empId) : []), [filteredUsers]);
  const areAllVisibleSelected = useMemo(() => visibleUserIds.length > 0 && visibleUserIds.every((id) => selectedUserIds.has(id)), [visibleUserIds, selectedUserIds]);
  const toggleSelectAllVisible = () => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (areAllVisibleSelected) {
        visibleUserIds.forEach((id) => next.delete(id));
      } else {
        visibleUserIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  // Previously, options were derived from current users causing them to disappear when filtering

  // Load routes when Assign Cab modal opens (location inferred from token)
  useEffect(() => {
    const fetchRoutes = async () => {
      setIsDropStationsLoading(true);
      try {
        const data = await routesApi.getAllRoutes();
        // Accept multiple shapes; extract array under data/content or the root
        let list = [];
        if (Array.isArray(data)) list = data;
        else if (data?.data && Array.isArray(data.data)) list = data.data;
        else if (data?.content && Array.isArray(data.content)) list = data.content;

        // Map to readable route labels (show timing instead of round)
        const routeLabels = list.map((r) => {
          const src = r?.source || 'Unknown';
          const dest = r?.destination || 'Unknown';
          const time = r?.timing ? ` (${r.timing})` : '';
          return `${src} → ${dest}${time}`;
        });
        setDropStationOptions(routeLabels);
      } catch (e) {
        console.warn('Failed to load routes:', e);
        setDropStationOptions([]);
      } finally {
        setIsDropStationsLoading(false);
      }
    };

    if (showAssignCabModal) {
      fetchRoutes();
    } else {
      // Reset on close
      setDropStationOptions([]);
      setIsDropStationsLoading(false);
      setAssignedVehiclesOptions([]);
      setIsVehiclesLoading(false);
    }
  }, [showAssignCabModal]);

  // Vehicle dropdown no longer loads via API

  

  // Handle status change for individual users
  const handleStatusChange = async (userId, newStatus) => {
    // If trying to deactivate, show confirmation modal
    if (newStatus === 'INACTIVE') {
      const user = users.find(u => (u.id || u.empId) === userId);
      setDeactivateData({
        userId: userId,
        userName: `${user?.firstName || ''} ${user?.secondName || ''}`.trim(),
        reason: ''
      });
      setShowDeactivateModal(true);
      return;
    }

    // If activating, proceed directly
    await updateUserStatus(userId, newStatus);
  };

  // Function to actually update user status
  const updateUserStatus = async (userId, newStatus, reason = '') => {
    try {
      // Update local state immediately for better UX
      setUserStatuses(prev => ({
        ...prev,
        [userId]: newStatus
      }));

      // Call the changeStatus API
      const response = await userApi.changeStatus(userId, newStatus);
      console.log('Status updated successfully:', response);
      
      setSuccessMessage(`User status updated to ${newStatus}`);
      setTimeout(() => setSuccessMessage(''), 3000);
      
    } catch (error) {
      console.error('Error updating user status:', error);
      // Revert the change if API call fails
      setUserStatuses(prev => ({
        ...prev,
        [userId]: prev[userId] === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE'
      }));
      setError('Failed to update user status. Please try again.');
    }
  };

  const handleCloseModal = () => {
    setShowAddUserModal(false);
    setFormData({
      firstName: '',
      secondName: '',
      email: '',
      phone: '',
      empId: '',
      password: '',
      role: '',
      department: '',
      location: ''
    });
    setError('');
    setSuccessMessage('');
  };

  const handleCloseDeactivateModal = () => {
    setShowDeactivateModal(false);
    setDeactivateData({
      userId: null,
      userName: '',
      reason: ''
    });
    setError('');
  };

  const handleDeactivateReasonChange = (e) => {
    setDeactivateData(prev => ({
      ...prev,
      reason: e.target.value
    }));
  };

  const handleConfirmDeactivation = async () => {
    if (!deactivateData.reason.trim()) {
      try {
        const { toast } = await import('react-toastify');
        toast.warn('Please provide a reason for deactivating the user.');
      } catch (e) {
        // Fallback to inline error if toast not available
        setError('Please provide a reason for deactivating the user.');
      }
      return;
    }

    setError('');
    setShowDeactivateModal(false);
    
    // Proceed with deactivation
    await updateUserStatus(deactivateData.userId, 'INACTIVE', deactivateData.reason);
    
    // Reset deactivate data
    setDeactivateData({
      userId: null,
      userName: '',
      reason: ''
    });
  };

  // Handle view user action
  const handleViewUser = (user) => {
    console.log('View user:', user);
    // Navigate to user details page
    const userId = user.id || user.empId;
    if (userId) {
      navigate(`/admin/community/users/${userId}`);
    } else {
      setError('Unable to view user details: User ID not found');
    }
  };

  // Handle edit user action
  const handleEditUser = (user) => {
    const userId = user.id || null;
    if (!userId) {
      setError('Cannot edit: user id not found');
      return;
    }
    setIsEditMode(true);
    setEditingUserId(userId);
    setShowAddUserModal(true);
    setError('');
    setSuccessMessage('');

    // Prefill with known row data immediately for responsiveness
    setFormData((prev) => ({
      ...prev,
      firstName: user.firstName || '',
      secondName: user.secondName || '',
      email: user.email || '',
      phone: user.phone || '',
      empId: user.empId || '',
      password: '',
      role: user.roleName || user.role || '',
      department: user.department || '',
      location: user.location || ''
    }));

    // Then fetch latest details using getUserQRCode for accuracy
    (async () => {
      try {
        const resp = await userApi.getUserQRCode(userId);
        const payload = resp?.data || resp; // support multiple shapes
        const details = payload?.user || payload?.details || payload; // attempt common keys
        if (details && typeof details === 'object') {
          setFormData((prev) => ({
            ...prev,
            firstName: details.firstName ?? prev.firstName,
            secondName: details.secondName ?? prev.secondName,
            email: details.email ?? prev.email,
            phone: details.phone ?? prev.phone,
            empId: details.empId ?? prev.empId,
            // keep password empty on edit
            role: details.roleName ?? details.role ?? prev.role,
            department: details.department ?? prev.department,
            location: details.location ?? prev.location
          }));
        }
      } catch (e) {
        console.warn('Failed to prefill from getUserQRCode:', e);
      }
    })();
  };

  const handleUnlockAccount = async (user) => {
    const userId = user.id;
    if (!userId) {
      setError('Cannot unlock: user ID not found');
      return;
    }

    try {
      const { toast } = await import('react-toastify');
      toast.info(`Unlocking account for ${user.firstName} ${user.secondName}...`, {
        position: "top-right",
        autoClose: 2000,
      });

      const response = await userApi.unlockAccount(userId);

      // Refresh the user list to show updated locked status
      await handleApplyFilters();

      const successMsg = response?.message || 'Account unlocked successfully';
      setSuccessMessage(successMsg);

      toast.success(successMsg, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });

    } catch (error) {
      console.error('Error unlocking account:', error);
      const errorMsg = error.response?.data?.message || error.message || 'Failed to unlock account';
      setError(errorMsg);

      const { toast } = await import('react-toastify');
      toast.error(errorMsg, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if user is authenticated
    const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    if (!token) {
      setError('You are not authenticated. Please log in again.');
      return;
    }
    
    // Basic validation
    const commonMissing = (!formData.firstName.trim() || !formData.secondName.trim() || !formData.email.trim() ||
        !formData.phone.trim() || !formData.empId.trim() || !formData.role);
    // Password is only required for non-REGISTERED_USER roles when creating
    const missingForCreate = (formData.role !== 'REGISTERED_USER' && !formData.password.trim());
    if (commonMissing || (!isEditMode && missingForCreate)) {
      setError('Please fill in all required fields.');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address.');
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      if (isEditMode) {
        // Call update API
        await userApi.updateUser(editingUserId, formData);
        const { toast } = await import('react-toastify');
        toast.success('User updated successfully!', { position: 'top-right', autoClose: 3000 });
        // Wait a moment to show the message before closing modal
        setTimeout(() => {
          handleCloseModal();
        }, 1000);
        await handleApplyFilters();
        return;
      }

      // Call the create user API (create mode)
      const response = await userApi.registerUser(formData);
      console.log('User created successfully:', response);
      
      // Show success message
      setSuccessMessage('User created successfully!');
      
      // Show success toast
      const { toast } = await import('react-toastify');
      toast.success('User created successfully!', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      
      // Refresh users list (stay on current page)
      handleApplyFilters(currentPage);
      
      // Close modal and reset form after a short delay
      setTimeout(() => {
        handleCloseModal();
        // TODO: Refresh the users list or add the new user to the current list
        // You might want to call a function to refresh the users data here
      }, 1500);
      
    } catch (error) {
      console.error('Error creating user:', error);
      if (error.message === 'No authentication token found') {
        setError('Your session has expired. Please log in again.');
        // Redirect to login after a short delay
        setTimeout(() => {
          window.location.href = '/signin';
        }, 2000);
      } else {
        const errorMsg = error.response?.data?.message || error.message || 'Failed to create user. Please try again.';
        setError(errorMsg);
        
        // Show error toast
        const { toast } = await import('react-toastify');
        toast.error(errorMsg, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="admin-view users-view">
      <PageHeader breadcrumbs={["Community", "Users"]} />

      {/* Compact Header with Filters */}
      <div className="page-header-compact">
        <div className="header-left-compact">
          <h2 className="page-title-compact">
            <MdPeople className="title-icon" />
            Users Management
          </h2>
        </div>
        <div className="header-filters-compact">
          <div className="input search-compact">
            <span className="icon"><MdSearch /></span>
            <input
              placeholder="Search by name, email, or ID..."
              value={filters.query}
              onChange={(e) => setFilters({ ...filters, query: e.target.value })}
            />
          </div>
          <select
            value={filters.department}
            onChange={(e) => setFilters({ ...filters, department: e.target.value })}
            className="filter-select-compact"
            disabled={isLoadingDepartments}
          >
            <option value="">
              {isLoadingDepartments ? 'Loading...' : 'All Departments'}
            </option>
            {allDepartments.map(dept => (
              <option key={dept} value={dept}>{dept}</option>
            ))}
          </select>
          <select
            value={filters.location}
            onChange={(e) => setFilters({ ...filters, location: e.target.value })}
            className="filter-select-compact"
          >
            <option value="">All Locations</option>
            {allLocations.map(loc => (
              <option key={loc} value={loc}>{loc}</option>
            ))}
          </select>
          <button className="btn-book-compact" onClick={handleAssignCab} title="Assign Cab">
            <MdDirectionsCar />
            <span>Assign Cab</span>
          </button>
          <button className="btn-book-compact" onClick={handleOpenBulkImport} title="Bulk Import">
            <MdUpload />
            <span>Bulk Import</span>
          </button>
          <button className="btn-book-compact" onClick={handleAddUser}>
            <MdPersonAdd />
            <span>Add User</span>
          </button>
        </div>
      </div>

      <div className="table card">
        <div className="table-head">
          <div
            className={`cell user sortable ${sortColumn === 'name' ? 'sorting' : ''} ${sortColumn === 'name' && sortDirection === 'desc' ? 'sort-desc' : ''}`}
            onClick={() => handleSort('name')}
          >
            User
            {sortColumn === 'name' && (
              sortDirection === 'asc' ? <MdArrowUpward className="sort-icon" /> : <MdArrowDownward className="sort-icon" />
            )}
          </div>
          <div
            className={`cell sortable ${sortColumn === 'email' ? 'sorting' : ''} ${sortColumn === 'email' && sortDirection === 'desc' ? 'sort-desc' : ''}`}
            onClick={() => handleSort('email')}
          >
            Email
            {sortColumn === 'email' && (
              sortDirection === 'asc' ? <MdArrowUpward className="sort-icon" /> : <MdArrowDownward className="sort-icon" />
            )}
          </div>
          <div
            className={`cell sortable ${sortColumn === 'role' ? 'sorting' : ''} ${sortColumn === 'role' && sortDirection === 'desc' ? 'sort-desc' : ''}`}
            onClick={() => handleSort('role')}
          >
            Role
            {sortColumn === 'role' && (
              sortDirection === 'asc' ? <MdArrowUpward className="sort-icon" /> : <MdArrowDownward className="sort-icon" />
            )}
          </div>
          <div
            className={`cell sortable ${sortColumn === 'location' ? 'sorting' : ''} ${sortColumn === 'location' && sortDirection === 'desc' ? 'sort-desc' : ''}`}
            onClick={() => handleSort('location')}
          >
            Location
            {sortColumn === 'location' && (
              sortDirection === 'asc' ? <MdArrowUpward className="sort-icon" /> : <MdArrowDownward className="sort-icon" />
            )}
          </div>
          <div
            className={`cell sortable ${sortColumn === 'department' ? 'sorting' : ''} ${sortColumn === 'department' && sortDirection === 'desc' ? 'sort-desc' : ''}`}
            onClick={() => handleSort('department')}
          >
            Department
            {sortColumn === 'department' && (
              sortDirection === 'asc' ? <MdArrowUpward className="sort-icon" /> : <MdArrowDownward className="sort-icon" />
            )}
          </div>
          <div
            className={`cell sortable ${sortColumn === 'status' ? 'sorting' : ''} ${sortColumn === 'status' && sortDirection === 'desc' ? 'sort-desc' : ''}`}
            onClick={() => handleSort('status')}
          >
            Status
            {sortColumn === 'status' && (
              sortDirection === 'asc' ? <MdArrowUpward className="sort-icon" /> : <MdArrowDownward className="sort-icon" />
            )}
          </div>
          <div className='cell'>Action</div>
          <div className="cell" style={{ width: 50 }}>
            <input
              type="checkbox"
              checked={areAllVisibleSelected}
              onChange={toggleSelectAllVisible}
              aria-label="Select all visible users"
            />
          </div>
        </div>
        {isTableLoading && Array.from({ length: 6 }).map((_, idx) => (
          <SkeletonRow key={idx} />
        ))}
        {!isTableLoading && filteredUsers && filteredUsers.length > 0 && filteredUsers.map((u, idx) => {
          console.log('Rendering user:', idx, u);
          return (
            <div className="table-row" key={idx}>
              <div className="cell user">
                <div className="avatar">{u.firstName?.[0] || '?'}</div>
                <div className="info">
                  <div className="name">{`${u.firstName || ''} ${u.secondName || ''}`.trim()}</div>
                  <div className="meta">ID: {u.empId || '-'}</div>
                </div>
              </div>
              <div className="cell">{u.email || '-'}</div>
              <div className="cell">{u.roleName || u.role || '-'}</div>
              <div className="cell">{u.location || '-'}</div>
              <div className="cell">{u.department || '-'}</div>
              <div className="cell">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <StatusToggle
                    id={`status-${u.id || u.empId}`}
                    isActive={(userStatuses[u.id || u.empId] || 'ACTIVE') === 'ACTIVE'}
                    onToggle={(next) => handleStatusChange(u.id || u.empId, next ? 'ACTIVE' : 'INACTIVE')}
                  />
                  {u.lockedUntil && new Date(u.lockedUntil) > new Date() && (
                    <span style={{
                      fontSize: '11px',
                      color: '#d32f2f',
                      fontWeight: '600',
                      backgroundColor: '#ffebee',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      display: 'inline-block'
                    }}>
                      <MdLock style={{ display: 'inline', marginRight: '4px', verticalAlign: 'middle' }} /> Locked
                    </span>
                  )}
                </div>
              </div>
              <div className="cell">
                <div className="action-buttons" style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                  <button
                    className="btn btn-small"
                    onClick={() => handleViewUser(u)}
                    title="View User"
                  >
                    <MdVisibility />
                  </button>
                  <button
                    className="btn btn-small btn-secondary"
                    onClick={() => handleEditUser(u)}
                    title="Edit User"
                  >
                    <MdEdit />
                  </button>
                  {u.lockedUntil && new Date(u.lockedUntil) > new Date() && (
                    <button
                      className="btn btn-small"
                      onClick={() => handleUnlockAccount(u)}
                      title="Unlock Account"
                      style={{
                        backgroundColor: '#4caf50',
                        color: 'white',
                        border: 'none'
                      }}
                    >
                      <MdLockOpen />
                    </button>
                  )}
                </div>
              </div>
              <div className="cell">
                <input
                  type="checkbox"
                  checked={isUserSelected(u.id || u.empId)}
                  onChange={() => toggleUserSelected(u)}
                  aria-label={`Select user ${u.firstName || ''} ${u.secondName || ''}`.trim()}
                />
              </div>
            </div>
          );
        })}
        {!isTableLoading && (!filteredUsers || filteredUsers.length === 0) && (
          <div className="table-row">
            <div className="cell" style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '16px' }}>
              No users found.
            </div>
          </div>
        )}
      </div>

      {/* Pagination Info */}
      {totalItems > 0 && (
        <div className="pagination-info" style={{ 
          marginBottom: '16px', 
          fontSize: '14px', 
          color: '#666',
          textAlign: 'center'
        }}>
          Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalItems)} of {totalItems} users
        </div>
      )}
      
      {/* Bulk Import Modal */}
      {showBulkImportModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>
                <MdUpload />
                Bulk Import Users
              </h3>
              <button
                className="modal-close"
                onClick={handleCloseBulkImport}
              >
                <MdClose />
              </button>
            </div>

            <div className="user-form">
              <div className="form-group">
                <label htmlFor="bulkFile">Upload Excel (.xlsx, .xls)</label>
                <input
                  id="bulkFile"
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleBulkFileChange}
                />
              </div>

              <div className="helper-text" style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                Make sure your file includes columns: firstName, secondName, email, phone, empId, role, department, location, password (optional for REGISTERED_USER).
              </div>

              {error && (
                <div className="error-message" style={{ color: 'red', marginTop: '12px' }}>{error}</div>
              )}
              {successMessage && (
                <div className="success-message" style={{ color: 'green', marginTop: '12px' }}>{successMessage}</div>
              )}

              <div className="form-actions">
                <button 
                  type="button" 
                  className="admin-btn admin-btn-secondary"
                  onClick={handleCloseBulkImport}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="admin-btn admin-btn-primary"
                  onClick={handleBulkImport}
                  disabled={isBulkImporting}
                >
                  {isBulkImporting ? 'Uploading...' : 'Upload'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Pagination 
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        showPagination={totalPages > 1}
      />

      

      {/* Add User Modal */}
      {showAddUserModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>
                {isEditMode ? <MdEdit /> : <MdPersonAdd />}
                {isEditMode ? 'Update User' : 'Add New User'}
              </h3>
              <button
                className="modal-close"
                onClick={handleCloseModal}
              >
                <MdClose />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="user-form user-form-modern">
              {/* Personal Information Section */}
              <div className="form-section">
                <div className="form-section-title">Personal Information</div>
                <div className="form-row form-row-3">
                  <div className="form-group">
                    <label htmlFor="firstName">First Name</label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="secondName">Second Name</label>
                    <input
                      type="text"
                      id="secondName"
                      name="secondName"
                      value={formData.secondName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="empId">Employee ID</label>
                    <input
                      type="text"
                      id="empId"
                      name="empId"
                      value={formData.empId}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Contact Information Section */}
              <div className="form-section">
                <div className="form-section-title">Contact Details</div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="email">Email Address</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="phone">Phone Number</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Work Information Section */}
              <div className="form-section">
                <div className="form-section-title">Work Assignment</div>
                <div className="form-row form-row-3">
                  <div className="form-group">
                    <label htmlFor="role">Access Role</label>
                    <select
                      id="role"
                      name="role"
                      value={formData.role}
                      onChange={handleInputChange}
                      required
                      disabled={isLoadingRoles}
                    >
                      <option value="">
                        {isLoadingRoles ? 'Loading roles...' : 'Select Role'}
                      </option>
                      {availableRoles.map((roleName) => (
                        <option key={roleName} value={roleName}>
                          {roleName}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="department">Department</label>
                    <select
                      id="department"
                      name="department"
                      value={formData.department}
                      onChange={handleInputChange}
                      required
                      disabled={isLoadingDepartments}
                    >
                      <option value="">
                        {isLoadingDepartments ? 'Loading departments...' : 'Select Department'}
                      </option>
                      {allDepartments && allDepartments.map((dept) => (
                        <option key={dept} value={dept}>{dept}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="location">Office Location</label>
                    <select
                      id="location"
                      name="location"
                      value={formData.location}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select Location</option>
                      {allLocations && allLocations.map((loc) => (
                        <option key={loc} value={loc}>{loc}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Security Section */}
              {formData.role !== 'REGISTERED_USER' && (
                <div className="form-section">
                  <div className="form-section-title">Security Credentials</div>
                  <div className="form-group">
                    <label htmlFor="password">Password {!isEditMode && <span style={{ color: 'red' }}>*</span>}</label>
                    <input
                      type="password"
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      required={!isEditMode}
                      placeholder="Enter secure password"
                    />
                    <div className="password-rules-hint" style={{
                      fontSize: '12px',
                      color: '#666',
                      marginTop: '8px',
                      padding: '10px',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '4px',
                      border: '1px solid #e9ecef'
                    }}>
                      <strong style={{ color: '#495057' }}>Password Requirements:</strong>
                      <ul style={{ margin: '4px 0 0 0', paddingLeft: '16px', lineHeight: '1.6' }}>
                        <li>At least 8 characters long</li>
                        <li>At least one uppercase letter (A-Z)</li>
                        <li>At least one lowercase letter (a-z)</li>
                        <li>At least one number (0-9)</li>
                        <li>At least one special character (!@#$%^&*)</li>
                      </ul>
                    </div>
                    {isEditMode && (
                      <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>
                        Leave blank to keep current password. If changed, user will be required to change password on next login.
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Helper text for REGISTERED_USER */}
              {formData.role === 'REGISTERED_USER' && (
                <div className="form-section">
                  <div className="helper-text" style={{ fontSize: '14px', color: '#666', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                    <strong>Note:</strong> REGISTERED_USER accounts do not require a password. These users can only book conference rooms via guest login and cannot access the system dashboard.
                  </div>
                </div>
              )}

              {error && (
                <div className="error-message">
                  {error}
                </div>
              )}
              {successMessage && (
                <div className="success-message">
                  {successMessage}
                </div>
              )}

              <div className="form-actions">
                <button 
                  type="button" 
                  className="admin-btn admin-btn-secondary"
                  onClick={handleCloseModal}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="admin-btn admin-btn-primary"
                  disabled={isLoading}
                >
                  {isEditMode ? (isLoading ? 'Updating...' : 'Update') : (isLoading ? 'Creating...' : 'Submit')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Deactivate User Confirmation Modal */}
      {showDeactivateModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Deactivate User</h3>
              <button 
                className="modal-close"
                onClick={handleCloseDeactivateModal}
              >
                ×
              </button>
            </div>
            
            <div className="deactivate-form">
              <div className="warning-message">
                <strong>Warning:</strong> You are about to deactivate user <strong>{deactivateData.userName}</strong>. 
                This action will prevent them from accessing the system.
              </div>

              <div className="form-group">
                <label htmlFor="deactivateReason">
                  Reason for deactivation <span style={{ color: 'red' }}>*</span>
                </label>
                <textarea
                  id="deactivateReason"
                  name="reason"
                  value={deactivateData.reason}
                  onChange={handleDeactivateReasonChange}
                  placeholder="Please provide a reason for deactivating this user..."
                  rows="4"
                  className="deactivate-reason-textarea"
                  required
                />
              </div>

              {error && (
                <div className="error-message">
                  {error}
                </div>
              )}

              <div className="form-actions">
                <button 
                  type="button" 
                  className="admin-btn admin-btn-secondary"
                  onClick={handleCloseDeactivateModal}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="admin-btn admin-btn-danger"
                  onClick={handleConfirmDeactivation}
                >
                  Deactivate User
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assign Cab Modal - UI only */}
      {showAssignCabModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Assign Cab</h3>
              <button 
                className="modal-close"
                onClick={handleCloseAssignCab}
              >
                ×
              </button>
            </div>

            <div className="user-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="dropStation">Assign Route</label>
                  <select
                    id="dropStation"
                    name="dropStation"
                    value={assignForm.dropStation}
                    onChange={handleAssignChange}
                    required
                  >
                    <option value="" disabled>
                      {isDropStationsLoading ? 'Loading routes...' : 'Select Route'}
                    </option>
                    {!isDropStationsLoading && dropStationOptions.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="vehicle">Assign Vehicles</label>
                  <select
                    id="vehicle"
                    name="vehicle"
                    value={assignForm.vehicle}
                    onChange={handleAssignChange}
                    required
                  >
                    <option value="" disabled>
                      {isVehiclesLoading ? 'Loading vehicles...' : 'Select Vehicle'}
                    </option>
                    {!isVehiclesLoading && assignedVehiclesOptions && assignedVehiclesOptions.map((v) => (
                      <option key={v} value={v}>{v}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-actions">
                <button 
                  type="button" 
                  className="admin-btn admin-btn-secondary"
                  onClick={handleCloseAssignCab}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="admin-btn admin-btn-primary"
                  onClick={handleAssignConfirm}
                  disabled={!assignForm.dropStation || !assignForm.vehicle || isAssigning}
                >
                  {isAssigning ? 'Assigning...' : 'Assign'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Users;


